import type { i18n as I18n, TFunction } from 'i18next';

import english from './en.translation.json';
import spanish from './es.translation.json';
import { missingInterpolation, registerFormatters } from './formatters';
import { toPortalLanguage } from './languageChoice';
import norwegian from './nb.translation.json';

const resources = {
    en: {
        translation: english,
        // Virtual Namespaces for English translations
        atoms: english.atoms,
        cageCharts: english.cageCharts,
        cageSettings: english.cageSettings,
        clientLocations: english.clientLocations,
        common: english.common,
        behaviorModule: english.behaviorModule,
        environmentalData: english.environmentalData,
        locationCompare: english.locationCompare,
        healthModule: english.healthModule,
        bioscopeStatus: english.bioscopeStatus,
        harvestReport: english.harvestReport,
        smoltReport: english.smoltReport,
        liceReport: english.liceReport,
        impersonation: english.impersonation,
        fishGroups: english.fishGroups,
    },
    nb: {
        translation: norwegian,
        // Virtual Namespaces for Norwegian translations
        atoms: norwegian.atoms,
        cageCharts: norwegian.cageCharts,
        cageSettings: norwegian.cageSettings,
        clientLocations: norwegian.clientLocations,
        common: norwegian.common,
        behaviorModule: norwegian.behaviorModule, // TODO: Add Norwegian translations
        environmentalData: norwegian.environmentalData,
        locationCompare: norwegian.locationCompare,
        healthModule: norwegian.healthModule,
        bioscopeStatus: norwegian.bioscopeStatus,
        harvestReport: norwegian.harvestReport,
        smoltReport: norwegian.smoltReport,
        liceReport: norwegian.liceReport,
        impersonation: norwegian.impersonation,
        fishGroups: norwegian.fishGroups,
    },
    es: {
        translation: spanish,
        // Virtual Namespaces for Spanish translations
        atoms: spanish.atoms,
        cageCharts: spanish.cageCharts,
        cageSettings: spanish.cageSettings,
        clientLocations: spanish.clientLocations,
        common: spanish.common,
        behaviorModule: spanish.behaviorModule, // TODO: Add Spanish translations
        environmentalData: spanish.environmentalData,
        locationCompare: spanish.locationCompare,
        healthModule: spanish.healthModule,
        bioscopeStatus: spanish.bioscopeStatus,
        harvestReport: spanish.harvestReport,
        smoltReport: spanish.smoltReport,
        liceReport: spanish.liceReport,
        impersonation: spanish.impersonation,
        fishGroups: spanish.fishGroups,
    },
};

export const NAMESPACES = [
    'translation',
    'atoms',
    'cageSettings',
    'clientLocations',
    'common',
    'behaviorModule',
    'environmentalData',
    'cageCharts',
    'locationCompare',
    'healthModule',
    'bioscopeStatus',
    'harvestReport',
    'smoltReport',
    'liceReport',
    'impersonation',
    'fishGroups',
];

const isValidLocale = (locale: string): boolean => {
    try {
        new Intl.Locale(locale);
        return true;
    } catch (e) {
        return false;
    }
};

// https://stackoverflow.com/questions/1026069/how-do-i-make-the-first-letter-of-a-string-uppercase-in-javascript
const capitalizeFirstLetter = (instance: I18n, str: string) => {
    if (!str) return str;
    try {
        const currentLang = instance.language;
        const localeStr = isValidLocale(currentLang) ? currentLang : 'en';
        const locale = new Intl.Locale(localeStr);
        return (
            str.toLocaleUpperCase(locale.toString()).charAt(0) +
            str.slice(1).toLocaleLowerCase(locale.toString())
        );
    } catch (e) {
        // Fallback to simple capitalization if anything goes wrong
        console.warn('Capitalization failed, falling back to simple case', e);
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
};

/**
 * Sets up an i18next instance the way the portal runs it, apart from language detection and
 * the React binding, which `index.ts` adds to the app's instance. Tests call it on an instance
 * of their own, so they exercise the shipped setup without the app's global one.
 */
export function initI18n(instance: I18n): Promise<TFunction> {
    const ready = instance
        .use({
            type: 'postProcessor',
            name: 'capitalizeFirstLetter',
            process: (value: string) => capitalizeFirstLetter(instance, value),
        })
        .init({
            resources,
            fallbackLng: 'en',
            defaultNS: 'translation',
            ns: NAMESPACES,
            missingInterpolationHandler: missingInterpolation,
            interpolation: { escapeValue: false },
            // The detector only reads the stored language. Only the user's own pick is stored
            // (`chooseLanguage`); a language a page switches to for itself is not.
            // Without a stored pick the browser's language applies, read as the portal's code.
            detection: { caches: [], convertDetectedLanguage: toPortalLanguage },
        });
    registerFormatters(instance);
    return ready;
}
